import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { useTable, useGlobalFilter, useSortBy } from 'react-table';
import SitecorecontextFactory from '../../../lib/SitecoreContextFactory';
import Search from '../../../assets/icons/Search.svg';
import ChevronSmallDown from '../../../assets/icons/ChevronSmallDown.svg';
import ChevronSmallDownOrange from '../../../assets/icons/ChevronSmallDownOrange.svg';
import PaginationStart from '../../../assets/icons/PaginationStart.svg';
import PaginationStartDisabled from '../../../assets/icons/PaginationStartDisabled.svg';
import PaginationEnd from '../../../assets/icons/PaginationEnd.svg';
import PaginationEndDisabled from '../../../assets/icons/PaginationEndDisabled.svg';
import Backward from '../../../assets/icons/Backward.svg';
import BackwardDisabled from '../../../assets/icons/BackwardDisabled.svg';
import Forward from '../../../assets/icons/Forward.svg';
import ForwardDisabled from '../../../assets/icons/ForwardDisabled.svg';
import Download from '../../../assets/icons/Download.svg';
import TableIcon from '../../../assets/icons/Table.svg';
import Filter from '../../../assets/icons/Filter16x16.svg';
import TextInput from '../../../lib/ui/form_elements/TextInput';
import TertiaryButton from '../../../lib/ui/buttons/TertiaryButton';
import { TABS, SORTING_DIRECTION } from '../utils';
import {
  FiltersAndTableContainer, TableContainer, StyledTable, TableDataCell, ReversedIcon, CellContainer, PaginationContainer,
  PageNumber, NoResults, StatusContainer, StatusLine,
} from './css';
import {
  TabsContainer, TabContainer, FiltersContainer, MobileActionsContainer, ActionsContainer,
} from '../css';
import { DropdownActions } from '../index';
import { ShipmentStatuses, Locales } from '../../../utils/enums';
import { routes } from '../../../utils/constants';

const Table = ({
  data,
  columns,
  activeTab,
  changeActiveTab,
  handleDownloadReportClick,
  setIsCustomizeMenuOpen,
  setIsFiltersMenuOpen,
  pageNumber,
  onPageChange,
  totalPages,
  numberOfFilters,
  fieldOrder,
  changeFieldOrder,
  handleSearch,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { globalFilter },
  } = useTable({ columns, data }, useGlobalFilter, useSortBy);

  const [globalFilterValue, setGlobalFilterValue] = useState(globalFilter);
  // Dirty fix for action menu bug in a single row table
  const [actionMenuOpened, setActionMenuOpened] = useState(false);

  const getPaginationRange = () => {
    // Constants...
    const START_PAGE = 1;
    const DEFAULT_RANGE = 5;

    // Initializations...
    let range = DEFAULT_RANGE;

    if (range > totalPages) {
      range = totalPages;
    }

    let start = pageNumber - Math.floor(range / 2);
    start = Math.max(start, START_PAGE);
    start = Math.min(start, START_PAGE + totalPages - range);

    return Array.from(
      { length: range },
      (element, index) => start + index,
    );
  };

  const rowClickHandler = (orderId, status, column) => {
    let shouldOpen = true;

    if (status === ShipmentStatuses.Expired || status === ShipmentStatuses.UnderReview || status === ShipmentStatuses.NotYetPickedUp) {
      shouldOpen = false;
    }

    if (column === 'actions') {
      shouldOpen = false;
    }

    if (shouldOpen) {
      window.open(`${routes.viewOrder}?probillNumber=${orderId}`);
    }
  };

  const handleClick = () => {
    if (document.querySelector('.single-row-table') && document.querySelector('.actions-menu>button')) {
      setTimeout(() => {
        const actionsOpened = document.querySelector('.actions-menu>button').ariaExpanded === 'true';
        setActionMenuOpened(actionsOpened);
      }, 100);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClick);
  }, []);

  return (
    <>
      <TabsContainer>
        <TabContainer selected={activeTab === TABS.ALL} onClick={() => changeActiveTab(TABS.ALL)}>{t('All')}</TabContainer>
        <TabContainer selected={activeTab === TABS.IN_PROGRESS} onClick={() => changeActiveTab(TABS.IN_PROGRESS)}>{t('InProgress')}</TabContainer>
        <TabContainer selected={activeTab === TABS.COMPLETED} onClick={() => changeActiveTab(TABS.COMPLETED)}>{t('Completed')}</TabContainer>
      </TabsContainer>

      <FiltersAndTableContainer>
        <FiltersContainer>

          {(SitecorecontextFactory.context.language === Locales.English) && (
            <TextInput
              placeholder="Search"
              icon={{ src: Search, alt: 'Search', onClick: () => handleSearch(globalFilterValue) }}
              input={{
                value: (globalFilterValue || ''),
                onChange: (event) => setGlobalFilterValue(event.target.value),
                onKeyDown: (event) => {
                  if (event.charCode === 13 || event.keyCode === 13) {
                    event.preventDefault();
                    event.stopPropagation();
                    handleSearch(globalFilterValue);
                  }
                },
              }}
            />
          )}
          {(SitecorecontextFactory.context.language === Locales.French) && (
            <TextInput
              placeholder="Rechercher"
              icon={{ src: Search, alt: 'Rechercher', onClick: () => handleSearch(globalFilterValue) }}
              input={{
                value: (globalFilterValue || ''),
                onChange: (event) => setGlobalFilterValue(event.target.value),
                onKeyDown: (event) => {
                  if (event.charCode === 13 || event.keyCode === 13) {
                    event.preventDefault();
                    event.stopPropagation();
                    handleSearch(globalFilterValue);
                  }
                },
              }}
            />
          )}

          <ActionsContainer>
            <TertiaryButton
              label={t('DownloadReport')}
              onClick={(e) => handleDownloadReportClick(e)}
              size="small"
              showArrow={false}
              iconSrc={Download}
              isIconLeftPosition
            />
            <TertiaryButton
              label={t('Customize')}
              onClick={() => setIsCustomizeMenuOpen(true)}
              size="small"
              showArrow={false}
              iconSrc={TableIcon}
              isIconLeftPosition
            />
            <TertiaryButton
              label={`${t('Filters')}${numberOfFilters ? (` (${numberOfFilters})`) : ''}`}
              onClick={() => setIsFiltersMenuOpen(true)}
              size="small"
              showArrow={false}
              iconSrc={Filter}
              isIconLeftPosition
            />
          </ActionsContainer>

          <MobileActionsContainer>
            <DropdownActions
              handleDownloadReportClick={handleDownloadReportClick}
              toggleCustomizeMenu={() => setIsCustomizeMenuOpen(true)}
              toggleFilterMenu={() => setIsFiltersMenuOpen(true)}
            />
          </MobileActionsContainer>
        </FiltersContainer>
        <TableContainer className={rows.length === 1 ? 'single-row-table' : ''}>
          {!(rows && rows.length) && (
            <NoResults>
              {t('NoResults')}
            </NoResults>
          )}

          {(data && data.length && totalPages > 1) ? (
            <PaginationContainer>
              <TertiaryButton
                label={t('First')}
                onClick={() => onPageChange(1)}
                size="small"
                showArrow={false}
                iconSrc={PaginationStart}
                iconSrcDisabled={PaginationStartDisabled}
                isIconLeftPosition
                disabled={pageNumber === 1}
              />
              <TertiaryButton
                label={t('Previous')}
                onClick={() => onPageChange(pageNumber - 1)}
                size="small"
                showArrow={false}
                iconSrc={Backward}
                iconSrcDisabled={BackwardDisabled}
                isIconLeftPosition
                disabled={pageNumber === 1}
              />
              {pageNumber && totalPages && getPaginationRange().map((page) => (
                <PageNumber
                  key={page}
                  selected={(pageNumber === page)}
                  onClick={() => onPageChange(page)}
                >
                  {page}
                </PageNumber>
              ))}
              <TertiaryButton
                label={t('Next')}
                onClick={() => onPageChange(pageNumber + 1)}
                size="small"
                showArrow={false}
                iconSrc={Forward}
                iconSrcDisabled={ForwardDisabled}
                disabled={pageNumber === totalPages}
              />
              <TertiaryButton
                label={t('Last')}
                onClick={() => onPageChange(totalPages)}
                size="small"
                showArrow={false}
                iconSrc={PaginationEnd}
                iconSrcDisabled={PaginationEndDisabled}
                disabled={pageNumber === totalPages}
              />
            </PaginationContainer>
          ) : null}

          <StyledTable {...getTableProps()}>
            <thead>
              {
              headerGroups.map((headerGroup, index) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, idx) => (

                    <th
                      key={column.id}
                      className={(idx < 2) ? `sticky table-header column-${idx}` : ''}
                      onClick={() => changeFieldOrder(column.name)}
                      {...column.getHeaderProps()}
                    >
                      {column.label}
                      {column.disableSortBy
                        ? null
                        : (fieldOrder[activeTab] && column.name === fieldOrder[activeTab].fieldName
                          && fieldOrder[activeTab].direction === SORTING_DIRECTION.DESC)
                          ? <img src={ChevronSmallDownOrange} alt="sort descending" />
                          : (fieldOrder[activeTab] && column.name === fieldOrder[activeTab].fieldName
                            && fieldOrder[activeTab].direction === SORTING_DIRECTION.ASC)
                            ? <ReversedIcon src={ChevronSmallDownOrange} alt="sort ascending" />
                            : <img src={ChevronSmallDown} alt="sort" />}
                    </th>
                  ))}
                </tr>
              ))
              }
            </thead>

            <tbody {...getTableBodyProps()}>
              {rows.map((row, index) => {
                prepareRow(row);

                return (
                  <tr {...row.getRowProps()} key={index} className={actionMenuOpened ? 'single-row' : ''}>
                    {row.cells.map((cell, idx) => {
                      let cellElement = cell.render('Cell');

                      const { orderId } = cell.row.original;
                      const orderStts = cell.row.original.status;

                      if (cell.column && (cell.column.name === 'shipper' || cell.column.name === 'consignee')) {
                        cellElement = (
                          <CellContainer>
                            <span>{cell.row.original[`${cell.column.name}Name`]}</span>
                            <div>
                              <div>{cell.row.original[`${cell.column.name}Address`]}</div>
                              <div>
                                {cell.row.original[`${cell.column.name}City`]}
                                ,
                                {' '}
                                {cell.row.original[`${cell.column.name}State`]}
                              </div>
                              <div>{cell.row.original[`${cell.column.name}ZipCode`]}</div>
                            </div>
                          </CellContainer>
                        );
                      } else if (cell.column && cell.column.name === 'status') {
                        cellElement = (
                          <StatusContainer>
                            {cell.row.original.status !== 'Open'
                            && <StatusLine bgWidth={cell.row.original.statusPercentage} bgColor={cell.row.original.statusColor} />}
                            <div>{cell.render('Cell')}</div>
                          </StatusContainer>
                        );
                      }

                      return (
                        <TableDataCell
                          key={index}
                          className={(idx < 2) ? `sticky table-cell column-${idx}` : ''}
                          onClick={() => rowClickHandler(orderId, orderStts, cell.column.name)}
                          topAligned={cell.column.name === 'status'}
                          textAlignCenter={cell.column.name === 'actions'}
                          {...cell.getCellProps()}
                        >
                          {cellElement}
                        </TableDataCell>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
        </TableContainer>
      </FiltersAndTableContainer>
    </>
  );
};

Table.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  activeTab: PropTypes.string,
  changeActiveTab: PropTypes.func,
  handleDownloadReportClick: PropTypes.func,
  setIsCustomizeMenuOpen: PropTypes.func,
  setIsFiltersMenuOpen: PropTypes.func,
  pageNumber: PropTypes.number,
  onPageChange: PropTypes.func,
  totalPages: PropTypes.number,
  numberOfFilters: PropTypes.number,
  fieldOrder: PropTypes.object,
  changeFieldOrder: PropTypes.func,
  handleSearch: PropTypes.func,
};

export default Table;
